import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  Timestamp,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../Firebase/firebaseconfig";
import { listProyectos } from "../../services/proyectosService";
import notify from "../common/notify";

const emptyForm = {
  nombre: "",
  ubicacion: "",
  descripcion: "",
  proyectoId: "",
  proyectoNombre: "",
};

export default function NuevoFraccionamientoModal({
  open,
  onClose,
  onCreated,
  onUpdated,
  fraccionamiento = null,
}) {
  const [form, setForm] = useState({
    ...emptyForm,
  });
  const [saving, setSaving] = useState(false);
  const [proyectos, setProyectos] = useState([]);
  const isEdit = Boolean(fraccionamiento?.id);

  const commitInChunks = async (updates) => {
    const chunkSize = 450;

    for (let i = 0; i < updates.length; i += chunkSize) {
      const batch = writeBatch(db);
      updates.slice(i, i + chunkSize).forEach(({ ref, patch }) => {
        batch.update(ref, patch);
      });
      await batch.commit();
    }
  };

  const syncContextoRelacionado = async (fraccionamientoId, patch) => {
    if (!fraccionamientoId) return;

    const [propsSnap, obrasSnap] = await Promise.all([
      getDocs(query(collection(db, "propiedades"), where("fraccionamientoId", "==", fraccionamientoId))),
      getDocs(query(collection(db, "obras"), where("fraccionamientoId", "==", fraccionamientoId))),
    ]);

    const commonPatch = {
      proyectoId: patch.proyectoId || "",
      proyectoNombre: patch.proyectoNombre || "",
      fraccionamientoNombre: patch.nombre || "",
      fraccNombre: patch.nombre || "",
      updatedAt: Timestamp.now(),
    };

    const updates = [];

    propsSnap.docs.forEach((item) => {
      const data = item.data() || {};
      if (data.activo === false) return;
      updates.push({
        ref: item.ref,
        patch: commonPatch,
      });
    });

    obrasSnap.docs.forEach((item) => {
      const data = item.data() || {};
      if (data.activo === false) return;
      updates.push({
        ref: item.ref,
        patch: {
          proyectoId: patch.proyectoId || "",
          proyectoNombre: patch.proyectoNombre || "",
          fraccionamientoNombre: patch.nombre || "",
          updatedAt: Timestamp.now(),
        },
      });
    });

    await commitInChunks(updates);
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setProyectos(await listProyectos());
      } catch (err) {
        console.error("Error cargando proyectos:", err);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (fraccionamiento) {
      setForm({
        nombre: fraccionamiento.nombre || "",
        ubicacion: fraccionamiento.ubicacion || "",
        descripcion: fraccionamiento.descripcion || "",
        proyectoId: fraccionamiento.proyectoId || "",
        proyectoNombre: fraccionamiento.proyectoNombre || "",
      });
      return;
    }

    setForm({ ...emptyForm });
  }, [open, fraccionamiento]);

  if (!open) return null;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleProyectoChange = (e) => {
    const proyecto = proyectos.find((p) => p.id === e.target.value);
    setForm((prev) => ({
      ...prev,
      proyectoId: proyecto?.id || "",
      proyectoNombre: proyecto?.nombre || "",
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!form.nombre || !form.ubicacion) {
      notify.error("Nombre y ubicación son obligatorios");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        const patch = {
          ...form,
          updatedAt: Timestamp.now(),
        };

        await updateDoc(doc(db, "fraccionamientos", fraccionamiento.id), patch);
        await syncContextoRelacionado(fraccionamiento.id, patch);
        onUpdated?.({ id: fraccionamiento.id, ...fraccionamiento, ...patch });
        onClose?.();
        return;
      }

      const ref = await addDoc(collection(db, "fraccionamientos"), {
        ...form,
        estado: "activo",
        fechaCreacion: Timestamp.now(),
      });

      onCreated?.({ id: ref.id, ...form, estado: "activo" });
      setForm({ ...emptyForm });
      onClose?.();
    } catch (err) {
      console.error(err);
      notify.error(isEdit ? "Error al actualizar fraccionamiento" : "Error al crear fraccionamiento");
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3>{isEdit ? "Editar fraccionamiento" : "Nuevo fraccionamiento"}</h3>

        <form onSubmit={onSubmit} style={{ display: "grid", gap: 8 }}>
          <input
            name="nombre"
            placeholder="Nombre"
            value={form.nombre}
            onChange={handleChange}
          />
          <input
            name="ubicacion"
            placeholder="Ubicación"
            value={form.ubicacion}
            onChange={handleChange}
          />
          <textarea
            name="descripcion"
            placeholder="Descripción"
            value={form.descripcion}
            onChange={handleChange}
          />
          <select value={form.proyectoId} onChange={handleProyectoChange}>
            <option value="">Proyecto relacionado (opcional)</option>
            {proyectos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </select>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" disabled={saving}>
              {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
    padding: 16,
  },
  modal: {
    background: "#fff",
    borderRadius: 12,
    padding: 16,
    width: "min(560px, 92vw)",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  },
};
